import React, { useEffect, useState } from 'react';
import api from '../../../services/api';
import { Table, Button, Modal, Form, Input, Alert, Tag, Space, Image, message } from 'antd';
import ModernImageUpload from '../../UI/ModernImageUpload';
import { resolveImageSrc, getImageForCard } from '../../../utils/images';

function VendorCategories() {
	const [categories, setCategories] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [formOpen, setFormOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form] = Form.useForm();

	const load = async () => {
		try {
			const response = await api.getCategories();
			setCategories(response.data || []);
		} catch (e) {
			setError('Failed to load categories');
			setCategories([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => { load(); }, []);

	const openCreate = () => {
		setEditing(null);
		form.resetFields();
		form.setFieldsValue({
			name: '', 
			description: '', 
			image: null,
			imagePreview: null,
			is_active: true
		});
		setFormOpen(true);
	};

	const openEdit = (category) => {
		setEditing(category);
		form.resetFields();
		
		form.setFieldsValue({
			name: category.name || '', 
			description: category.description || '', 
			image: category.image,
			imagePreview: category.image,
			is_active: category.is_active
		});
		setFormOpen(true);
	};

	const onSubmit = async () => {
		try {
			const values = await form.validateFields();
			
			// Create FormData for file upload
			const formData = new FormData();
			
			// Add all form fields to FormData
			Object.keys(values).forEach(key => {
				if (key === 'image' && values[key] instanceof File) {
					// Add the actual file
					formData.append('image', values[key]);
				} else if (key !== 'image' && key !== 'imageVariants' && key !== 'imagePreview') {
					// Add other fields as strings
					formData.append(key, values[key]);
				}
			});
			
			if (editing) {
				await api.updateCategoryWithFile(editing.id, formData);
				message.success('Category updated successfully');
			} else {
				await api.createCategoryWithFile(formData);
				message.success('Category created successfully');
			}
			setFormOpen(false);
			await load();
		} catch (err) {
			message.error(err.message || 'Failed to save category');
		}
	};

	const onDelete = async (id) => {
		if (!confirm('Delete this category? This action cannot be undone.')) return;
		try {
			await api.deleteCategory(id);
			message.success('Category deleted successfully');
			await load();
		} catch (err) {
			message.error(err.message || 'Delete failed');
		}
	};

	if (loading) {
		return (
			<div className="min-h-[50vh] flex items-center justify-center">
				<div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-500"></div>
			</div>
		);
	}

	const columns = [
		{ 
			title: 'Image', 
			dataIndex: 'image', 
			key: 'image', 
			width: 80,
			render: (image, record) => {
				return (
					<Image
						src={getImageForCard(record)}
						alt={record.name}
						width={60}
						height={60}
						className="rounded object-cover"
						fallback="/images/placeholder.svg"
					/>
				);
			}
		},
		{ title: 'Name', dataIndex: 'name', key: 'name' },
		{ title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
		{ 
			title: 'Status', 
			dataIndex: 'is_active', 
			key: 'status',
			render: (isActive) => (
				<Tag color={isActive ? 'green' : 'red'}>
					{isActive ? 'Active' : 'Inactive'}
				</Tag>
			)
		},
		{ title: 'Sort Order', dataIndex: 'sort_order', key: 'sort_order' },
		{ title: '', key: 'actions', render: (_, category) => (
			<Space>
				<Button size="small" onClick={() => openEdit(category)}>Edit</Button>
				<Button size="small" danger onClick={() => onDelete(category.id)}>Delete</Button>
			</Space>
		)},
	];

	return (
		<div className="max-w-7xl mx-auto p-6">
			<div className="flex items-center justify-between mb-4">
				<h1 className="text-2xl font-bold">Manage Categories</h1>
				<Button type="primary" onClick={openCreate}>Add Category</Button>
			</div>
			{error && (<Alert type="error" message={error} className="mb-4" />)}

			<Table 
				columns={columns} 
				dataSource={categories} 
				rowKey="id" 
				pagination={{ pageSize: 10 }} 
				scroll={{ x: true }} 
			/>

			<Modal
				title={editing ? 'Edit Category' : 'Add Category'}
				open={formOpen}
				onCancel={() => setFormOpen(false)}
				onOk={onSubmit}
				okText="Save"
				destroyOnClose
				width={600}
			>
				<Form form={form} layout="vertical">
					<Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter category name' }]}>
						<Input placeholder="Enter category name" />
					</Form.Item>
					
					<Form.Item name="description" label="Description">
						<Input.TextArea rows={3} placeholder="Enter category description" />
					</Form.Item>
					
					<Form.Item name="image" label="Category Image">
						<ModernImageUpload
							currentImage={form.getFieldValue('imagePreview') || form.getFieldValue('image')}
							currentImageVariants={form.getFieldValue('imageVariants')}
							onUpload={async (file) => {
								// Store the actual File object for form submission
								form.setFieldValue('image', file);
								// Also store preview data for display
								form.setFieldValue('imagePreview', URL.createObjectURL(file));
								return { file_path: URL.createObjectURL(file) };
							}}
							onRemove={() => {
								form.setFieldValue('image', null);
								form.setFieldValue('imageVariants', null);
								form.setFieldValue('imagePreview', null);
							}}
						/>
					</Form.Item>
					
					<Form.Item name="is_active" valuePropName="checked" label="Active">
						<input type="checkbox" className="mr-2" />
						<span>Make this category active</span>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
}

export default VendorCategories;
